import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../utils/apiMessages';
import { useCityInView } from './city/useHubCity';

const EMPTY = {
  services: { items: [], total: 0 },
  commerces: { items: [], total: 0 },
  posts: { items: [], total: 0 },
  events: { items: [], total: 0 },
};

/**
 * One search across the whole hub, with the term in the URL.
 *
 * The URL is the source of truth, as it is for the directory: a search is
 * something people send to a neighbour, and pressing back has to return to the
 * previous one rather than to an empty field.
 *
 * Nothing is asked of the API below two characters — it refuses them, and
 * rightly — so an empty or single-letter term is simply "no search yet", not
 * an error to show.
 */
export function useSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cityParam } = useCityInView();
  const term = (searchParams.get('q') || '').trim();

  const [results, setResults] = useState(EMPTY);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (term.length < 2) {
      setResults(EMPTY);
      setTotal(0);
      setError('');
      setLoading(false);
      return undefined;
    }

    const load = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams({ q: term });
        // A visitor says which city they are looking at; a resident's account
        // already says it.
        if (cityParam) params.set('city', cityParam);

        const response = await apiCall(`/search?${params.toString()}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          const violation = data.violations ? Object.values(translateViolations(data.violations))[0] : null;

          throw new Error(
            violation || translateApiMessage(data.error) || 'La recherche n’a pas abouti.'
          );
        }

        setResults({ ...EMPTY, ...(data.data || {}) });
        setTotal(data.meta?.total ?? 0);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setResults(EMPTY);
          setTotal(0);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [term, cityParam]);

  /** Searching again from the results page replaces the term in the URL. */
  const setTerm = (next) => {
    const params = new URLSearchParams();

    if (next.trim()) {
      params.set('q', next.trim());
    }

    setSearchParams(params, { replace: true });
  };

  return { term, results, total, loading, error, setTerm };
}
